import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Card,
  Group,
  TextInput,
  Select,
  MultiSelect,
  Switch,
  Stack,
  Alert,
  Badge,
  PasswordInput,
} from '@mantine/core';
import { IconPlus, IconAlertCircle } from '@tabler/icons-react';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FormModal } from '../components/ui/FormModal';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { usersApi } from '../services/usersApi';
import { companiesApi } from '../services/companiesApi';
import { plantsApi } from '../services/plantsApi';
import { getApiErrorMessage } from '../utils/errorHandling';
import type { User, UserFormData, Company, Plant } from '../types/entities';
import { Role, RoleLabels, ReportCategory, ReportCategoryLabels } from '../types/entities';

const roleOptions = Object.values(Role).map((value) => ({
  value,
  label: RoleLabels[value],
}));

const reportCategoryOptions = Object.values(ReportCategory).map((value) => ({
  value,
  label: ReportCategoryLabels[value],
}));

function syncFiltersToUrl(
  filters: { company: string | null; role: string | null; plant: string | null; category: string | null },
  setSearchParams: (params: URLSearchParams) => void,
) {
  const params = new URLSearchParams();
  if (filters.company) params.set('company', filters.company);
  if (filters.role) params.set('role', filters.role);
  if (filters.plant) params.set('plant', filters.plant);
  if (filters.category) params.set('category', filters.category);
  setSearchParams(params);
}

export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // Filter state — initialised from the URL query parameters.
  const [filterCompany, setFilterCompany] = useState<string | null>(() => searchParams.get('company'));
  const [filterRole, setFilterRole] = useState<string | null>(() => searchParams.get('role'));
  const [filterPlant, setFilterPlant] = useState<string | null>(() => searchParams.get('plant'));
  const [filterCategory, setFilterCategory] = useState<string | null>(() => searchParams.get('category'));

  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    roles: [Role.USER],
    isActive: true,
    company: null,
    plants: [],
    enabledReportCategories: [],
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Static lookup data (companies, plants) — loaded once, not filter-dependent.
  const fetchStaticData = useCallback(async () => {
    try {
      const [companiesData, plantsData] = await Promise.all([
        companiesApi.getAll(),
        plantsApi.getAll(),
      ]);
      setCompanies(companiesData);
      setPlants(plantsData);
    } catch {
      setError('Fehler beim Laden der Daten');
    }
  }, []);

  // Users are filtered server-side; refetched whenever a filter changes.
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await usersApi.getAll({
        company: filterCompany,
        role: filterRole,
        plant: filterPlant,
        category: filterCategory,
      });
      setUsers(usersData);
    } catch {
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [filterCompany, filterRole, filterPlant, filterCategory]);

  useEffect(() => {
    fetchStaticData();
  }, [fetchStaticData]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Keep the URL query parameters in sync with the active filters.
  useEffect(() => {
    if (!initialLoad) {
      syncFiltersToUrl(
        { company: filterCompany, role: filterRole, plant: filterPlant, category: filterCategory },
        setSearchParams,
      );
    }
  }, [filterCompany, filterRole, filterPlant, filterCategory, initialLoad, setSearchParams]);

  const companyOptions = [
    { value: '', label: 'Kein Unternehmen' },
    ...companies.map((c) => ({
      value: `/api/companies/${c.id}`,
      label: c.name,
    })),
  ];

  const filterCompanyOptions = [
    { value: '', label: 'Alle Unternehmen' },
    ...companies.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  const filterRoleOptions = [
    { value: '', label: 'Alle Rollen' },
    ...roleOptions,
  ];

  const filterPlantOptions = [
    { value: '', label: 'Alle Werke' },
    ...plants.map((p) => ({ value: String(p.id), label: p.name })),
  ];

  const filterCategoryOptions = [
    { value: '', label: 'Alle Meldungen' },
    ...reportCategoryOptions,
  ];

  // Filter plants based on selected company in form
  const getAvailablePlants = () => {
    if (!formData.company) return [];
    const companyId = formData.company.split('/').pop();
    return plants
      .filter((p) => p.company?.id === Number(companyId))
      .map((p) => ({
        value: `/api/plants/${p.id}`,
        label: p.name,
      }));
  };


  const openCreateModal = () => {
    setSelectedUser(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      roles: [Role.USER],
      isActive: true,
      company: null,
      plants: [],
      enabledReportCategories: [],
      plainPassword: '',
    });
    setFormError(null);
    setFormModalOpen(true);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      roles: user.roles,
      isActive: user.isActive,
      company: user.company ? `/api/companies/${user.company.id}` : null,
      plants: user.plants?.map((p) => `/api/plants/${p.id}`) || [],
      enabledReportCategories: user.enabledReportCategories ?? [],
    });
    setFormError(null);
    setFormModalOpen(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleCompanyChange = (value: string | null) => {
    setFormData({
      ...formData,
      company: value || null,
      plants: [], // Clear plants when company changes
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      const submitData = { ...formData };
      // Don't send empty password on update
      if (selectedUser && !submitData.plainPassword) {
        delete submitData.plainPassword;
      }

      if (selectedUser) {
        await usersApi.update(selectedUser.id, submitData);
      } else {
        await usersApi.create(submitData);
      }
      setFormModalOpen(false);
      fetchUsers();
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setSaving(true);

    try {
      await usersApi.delete(selectedUser.id);
      setDeleteModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Fehler beim Löschen des Benutzers'));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<User>[] = [
    { key: 'email', label: 'E-Mail' },
    {
      key: 'name',
      label: 'Name',
      render: (user) => `${user.firstName} ${user.lastName}`,
    },
    {
      key: 'roles',
      label: 'Rollen',
      render: (user) => (
        <Group gap={4}>
          {user.roles
            .filter((r) => r !== 'ROLE_USER')
            .map((role) => (
              <Badge key={role} size="sm" variant="light">
                {RoleLabels[role as Role] || role}
              </Badge>
            ))}
          {user.roles.filter((r) => r !== 'ROLE_USER').length === 0 && (
            <Badge size="sm" variant="light">
              {RoleLabels[Role.USER]}
            </Badge>
          )}
        </Group>
      ),
    },
    {
      key: 'enabledReportCategories',
      label: 'Freigeschaltete Meldungen',
      render: (user) => {
        const categories = user.enabledReportCategories ?? [];
        if (categories.length === 0) return '-';
        return (
          <Group gap={4}>
            {categories.map((category) => (
              <Badge key={category} size="sm" variant="light">
                {ReportCategoryLabels[category as ReportCategory] || category}
              </Badge>
            ))}
          </Group>
        );
      },
    },
    {
      key: 'company',
      label: 'Unternehmen',
      render: (user) => user.company?.name ?? '-',
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (user) => <StatusBadge status={user.isActive ? 'aktiv' : 'inaktiv'} />,
    },
  ];

  return (
    <Stack>
      <PageHeader
        title="Benutzer verwalten"
        breadcrumb={{ label: 'Administration' }}
        rightSection={
          <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
            Neuer Benutzer
          </Button>
        }
      />

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}

      <Card>
        <Group>
          <Select
              placeholder="Nach Unternehmen filtern"
              data={filterCompanyOptions}
              value={filterCompany}
              onChange={setFilterCompany}
              clearable
              style={{ width: 200 }}
          />
          <Select
              placeholder="Nach Rolle filtern"
              data={filterRoleOptions}
              value={filterRole}
              onChange={setFilterRole}
              clearable
              style={{ width: 200 }}
          />
          <Select
              placeholder="Nach Werk filtern"
              data={filterPlantOptions}
              value={filterPlant}
              onChange={setFilterPlant}
              clearable
              style={{ width: 200 }}
          />
          <Select
              placeholder="Nach Meldung filtern"
              data={filterCategoryOptions}
              value={filterCategory}
              onChange={setFilterCategory}
              clearable
              style={{ width: 200 }}
          />
        </Group>
      </Card>

      <Card>
        <DataTable
          data={users}
          columns={columns}
          loading={loading}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
          getRowKey={(user) => user.id}
          emptyMessage="Keine Benutzer vorhanden"
        />
      </Card>

      <FormModal
        opened={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={selectedUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <Stack>
            {formError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {formError}
              </Alert>
            )}
            <TextInput
              label="E-Mail"
              placeholder="email@example.com"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Group grow>
              <TextInput
                label="Vorname"
                placeholder="Vorname"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
              <TextInput
                label="Nachname"
                placeholder="Nachname"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Group>
            <PasswordInput
              label={selectedUser ? 'Neues Passwort (optional)' : 'Passwort'}
              placeholder="********"
              value={formData.plainPassword || ''}
              onChange={(e) => setFormData({ ...formData, plainPassword: e.target.value })}
              required={!selectedUser}
            />
            <MultiSelect
              label="Rollen"
              placeholder="Rollen auswählen"
              data={roleOptions}
              value={formData.roles}
              onChange={(value) => setFormData({ ...formData, roles: value as Role[] })}
              required
            />
            <Select
              label="Unternehmen"
              placeholder="Unternehmen auswählen"
              data={companyOptions}
              value={formData.company || ''}
              onChange={handleCompanyChange}
              clearable
            />
            {formData.company && (
              <MultiSelect
                label="Werke"
                placeholder="Werke auswählen"
                data={getAvailablePlants()}
                value={formData.plants || []}
                onChange={(value) => setFormData({ ...formData, plants: value })}
              />
            )}
            <MultiSelect
              label="Freigeschaltete Meldungen"
              placeholder="Meldungskategorien auswählen"
              data={reportCategoryOptions}
              value={formData.enabledReportCategories}
              onChange={(value) =>
                setFormData({ ...formData, enabledReportCategories: value })
              }
            />
            <Switch
              label="Aktiv"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.currentTarget.checked })}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setFormModalOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={saving}>
                {selectedUser ? 'Speichern' : 'Erstellen'}
              </Button>
            </Group>
          </Stack>
        </form>
      </FormModal>

      <ConfirmModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Benutzer löschen"
        message={`Möchten Sie den Benutzer "${selectedUser?.firstName} ${selectedUser?.lastName}" wirklich löschen?`}
        loading={saving}
      />
    </Stack>
  );
}
